import  { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  selectCampaignLogsFilters,
  selectCampaignsFilters,
  selectCampaignTemplatesFilters,
  setCampaignLogsUser,
  setCampaignsUser,
  setCampaignTemplatesUser,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentPage } from "../../../redux/slices/allCampaignSlice";
import { useGetUsersQuery } from "../../../redux/services/userManagementApi";

interface User {
  user: {
    id: string;
    name: string;
  };
}

const CampaignUserFilter = ({
  onClose,
  campaignTemplates = false,
  campaignLogs = false,
}: {
  onClose: () => void;
  campaignTemplates?: boolean;
  campaignLogs?: boolean;
}) => {
  const campaignTemplatesFilters = useSelector(selectCampaignTemplatesFilters);
  const campaignLogsFilters = useSelector(selectCampaignLogsFilters);
  const campaignsFilters = useSelector(selectCampaignsFilters);

  const selectedUser = campaignTemplates
    ? campaignTemplatesFilters
    : campaignLogs
    ? campaignLogsFilters
    : campaignsFilters;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState<
    SearchableDropdownOption[]
  >([]);
  const [selectedUserId, setSelectedUserId] = useState<string>(
    selectedUser.user.value as string
  );
  const dispatch = useDispatch();

  const { data: users, isLoading, isFetching } = useGetUsersQuery({
    current_page: 1,
    limit: 10000,
    client_codes: [],
    is_org_request: false,
    status: ["active", "inactive"],
  });

  // Transform users data to dropdown options
  const transformedUsers = useMemo(
    () =>
      users?.user_list?.map((user: User) => ({
        value: user?.user?.id,
        label: user?.user?.name,
      })) || [],
    [users?.user_list]
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredUsers(transformedUsers);
        return;
      }

      const filtered = transformedUsers.filter(
        (user: SearchableDropdownOption) =>
          user.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredUsers(filtered);
    },
    [transformedUsers]
  );

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // Initialize filtered users
  useEffect(() => {
    setFilteredUsers(transformedUsers);
  }, [transformedUsers]);

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId === selectedUserId ? "" : userId);
  };

  const handleApply = () => {
    if (selectedUserId === selectedUser.user.value) {
      return;
    }
    dispatch(
      campaignTemplates
        ? setCampaignTemplatesUser({
            value: selectedUserId,
            displayValue: transformedUsers.find(
              (u: SearchableDropdownOption) => u.value === selectedUserId
            )?.label,
          })
        : campaignLogs
        ? setCampaignLogsUser({
            value: selectedUserId,
            displayValue: transformedUsers.find(
              (u: SearchableDropdownOption) => u.value === selectedUserId
            )?.label,
          })
        : setCampaignsUser({
            value: selectedUserId,
            displayValue: transformedUsers.find(
              (u: SearchableDropdownOption) => u.value === selectedUserId
            )?.label,
          })
    );
    dispatch(setCurrentPage(1));
    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search user"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected User Display */}
      {selectedUserId && (
        <div className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {
                transformedUsers.find(
                  (u: SearchableDropdownOption) => u.value === selectedUserId
                )?.label
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="flex flex-col max-h-[600px] ">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Users
        </div>

        <div className="overflow-y-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading users..." : "Fetching users..."}
              </div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No users found" : "No users available"}
              </div>
            </div>
          ) : (
            filteredUsers
              ?.filter(
                (user: SearchableDropdownOption) =>
                  user.value !== selectedUserId
              )
              ?.map((user: SearchableDropdownOption) => (
                <div
                  key={user.value}
                  onClick={() => handleUserSelect(user.value)}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {user.label}
                    </div>
                  </div>
                  {selectedUserId === user.value && (
                    <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={selectedUserId === selectedUser.user.value}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignUserFilter;
